// Lightweight theme switcher. Themes are applied by setting
// `data-theme` on <html>. CSS overrides live in src/index.css.

import { supabase } from "@/integrations/supabase/client";

export type AppTheme = "default" | "emerald" | "greenblack";

export const APP_THEMES: { id: AppTheme; labelKey: string }[] = [
  { id: "default", labelKey: "admin.appearance.themes.default" },
  { id: "emerald", labelKey: "admin.appearance.themes.emerald" },
  { id: "greenblack", labelKey: "admin.appearance.themes.greenblack" },
];

const STORAGE_KEY = "app_theme";
const DEFAULT_SETTING_KEY = "default_theme";

function isAppTheme(v: unknown): v is AppTheme {
  return v === "default" || v === "emerald" || v === "greenblack";
}

export function getStoredTheme(): AppTheme | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return isAppTheme(v) ? v : null;
}

export function getTheme(): AppTheme {
  return getStoredTheme() ?? "default";
}

export function applyTheme(theme: AppTheme) {
  if (typeof document === "undefined") return;
  if (theme === "default") {
    document.documentElement.removeAttribute("data-theme");
  } else {
    document.documentElement.setAttribute("data-theme", theme);
  }
}

export function setTheme(theme: AppTheme) {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, theme);
  }
  applyTheme(theme);
}

export function clearThemeOverride() {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(STORAGE_KEY);
  }
}

export async function fetchDefaultTheme(): Promise<AppTheme> {
  try {
    const { data } = await supabase
      .from("app_settings")
      .select("value")
      .eq("key", DEFAULT_SETTING_KEY)
      .maybeSingle();
    const v = isAppTheme(data?.value) ? (data!.value as AppTheme) : "default";
    if (typeof window !== "undefined") {
      window.localStorage.setItem("app_theme_default_cache", v);
    }
    return v;
  } catch {
    return "default";
  }
}

export async function setDefaultTheme(theme: AppTheme): Promise<void> {
  await supabase
    .from("app_settings")
    .upsert({ key: DEFAULT_SETTING_KEY, value: theme, updated_at: new Date().toISOString() });
  if (typeof window !== "undefined") {
    window.localStorage.setItem("app_theme_default_cache", theme);
  }
}

/** Applies the global default theme if the user has no personal override. */
export async function applyDefaultThemeIfUnset(): Promise<void> {
  if (getStoredTheme() !== null) return;
  const def = await fetchDefaultTheme();
  const current = (typeof document !== "undefined"
    ? document.documentElement.getAttribute("data-theme")
    : null);
  const currentTheme: AppTheme = isAppTheme(current) ? current : "default";
  if (currentTheme !== def) applyTheme(def);
}

/**
 * Fetch the signed-in user's theme override from their profile and apply it.
 * If they have no value stored, fall back to the global default.
 * Local storage mirrors the DB value so future loads are instant.
 */
export async function syncUserTheme(userId: string): Promise<void> {
  try {
    const { data } = await supabase
      .from("profiles")
      .select("theme")
      .eq("id", userId)
      .maybeSingle();
    const v = (data as { theme?: string | null } | null)?.theme;
    if (isAppTheme(v)) {
      if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, v);
      applyTheme(v);
    } else {
      clearThemeOverride();
      await applyDefaultThemeIfUnset();
    }
  } catch {
    // ignore; keep whatever is already applied
  }
}

/** Persist the user's theme choice. Pass null to clear (use global default). */
export async function persistUserTheme(userId: string, theme: AppTheme | null): Promise<void> {
  await supabase.from("profiles").update({ theme }).eq("id", userId);
}
